import React, { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, MoveUpRight, Pause, Play, RotateCcw } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import "./HolomExplorer.css";
import holomModel from "../holom-model.json";

const HOME = "145deg 62deg 105%";
const LABELS = [
  { id: "wing", title: "Fixed-wing efficiency", description: "A 3.6 m wingspan for efficient forward flight and extended mission coverage.", position: "1.02m 0.025m 0.15m", normal: "0 1 0", orbit: HOME },
  { id: "lift", title: "Vertical lift", description: "Dedicated electric lift propellers enable takeoff and landing without a runway.", position: "0.624m 0.084m -0.429m", normal: "0 1 0", orbit: "145deg 48deg 105%" },
  { id: "airframe", title: "Composite airframe", description: "A carbon fiber composite structure brings the aircraft systems together in one platform.", position: "0m 0.15m -0.25m", normal: "0 1 0", orbit: "125deg 70deg 105%" },
  { id: "tail", title: "V-tail", description: "Explore the tail surfaces and the aircraft’s lift + cruise architecture.", position: "0.25m 0.275m 1.18m", normal: "0 1 0", orbit: "-35deg 60deg 105%" },
];

export default function HolomExplorer() {
  const { language, t } = useI18n();
  const es = language === "es";
  const text = (en, spanish) => es ? spanish : en;
  const viewer = useRef(null);
  const [phase, setPhase] = useState("idle");
  const [active, setActive] = useState(LABELS[0].id);
  const [visible, setVisible] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [orbit, setOrbit] = useState(HOME);
  const selected = LABELS.find((label) => label.id === active);
  const ready = phase === "ready";

  async function load() {
    setPhase("loading");
    try {
      await import("@google/model-viewer");
      setPhase("model");
    } catch { setPhase("error"); }
  }

  useEffect(() => {
    const element = viewer.current;
    if (!element) return undefined;
    const loaded = () => setPhase("ready");
    const failed = () => setPhase("error");
    element.addEventListener("load", loaded);
    element.addEventListener("error", failed);
    if (element.loaded) loaded();
    return () => {
      element.removeEventListener("load", loaded);
      element.removeEventListener("error", failed);
    };
  }, [phase]);

  useEffect(() => {
    if (!rotating) return undefined;
    const section = viewer.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (section) section.autoRotate = entry.isIntersecting && !document.hidden;
    });
    if (section) observer.observe(section);
    const pauseHidden = () => { if (document.hidden) setRotating(false); };
    document.addEventListener("visibilitychange", pauseHidden);
    return () => { observer.disconnect(); document.removeEventListener("visibilitychange", pauseHidden); };
  }, [rotating]);

  function changeView(next) {
    setRotating(false);
    setOrbit(next);
    // Imperative assignment also restores a preset after the user has dragged the camera.
    if (viewer.current) { viewer.current.resetTurntableRotation(); viewer.current.cameraOrbit = next; }
  }

  function select(label) {
    setActive(label.id);
    changeView(label.orbit);
  }

  return (
    <section className="holom-section holom-explorer" id="explore-3d" aria-labelledby="holom-explorer-title">
      <div className="holom-container">
        <div className="holom-explorer__heading">
          <div className="holom-section-heading holom-section-heading--light">
            <p className="holom-eyebrow">{text("THE AIRCRAFT, UP CLOSE", "LA AERONAVE, DE CERCA")}</p>
            <h2 id="holom-explorer-title">{text("Every angle. Every detail.", "Cada ángulo. Cada detalle.")}</h2>
            <p>{text("Rotate Holom, explore its architecture and discover the details that make the mission possible.", "Gira Holom, explora su arquitectura y descubre los detalles que hacen posible la misión.")}</p>
          </div>
          <span className="holom-explorer__badge"><span />{text("INTERACTIVE 3D", "EXPLORACIÓN 3D")}</span>
        </div>
        <div className="holom-explorer__layout">
          <div className="holom-explorer__stage">
            <div className="holom-explorer__stage-label"><span>HOLOM</span><span>360° / LIFT + CRUISE</span></div>
            {["model", "ready"].includes(phase) ? (
              <model-viewer ref={viewer} src={holomModel.src} alt={text("Interactive 3D Holom aircraft. Drag or use arrow keys to rotate; pinch or scroll to zoom.", "Aeronave Holom en 3D. Arrastra o usa las flechas para girar; pellizca o desplaza para acercar.")}
                camera-controls="" touch-action="pan-y" disable-pan="" camera-orbit={orbit}
                min-camera-orbit="auto 8deg 45%" max-camera-orbit="auto 165deg 200%" field-of-view="30deg"
                shadow-intensity="0.7" shadow-softness="1" exposure="1.1" environment-image="neutral"
                auto-rotate={rotating ? "" : undefined} rotation-per-second="12deg" interaction-prompt="none">
                {visible ? LABELS.map((label, index) => <button key={label.id} slot={`hotspot-${label.id}`} data-position={label.position} data-normal={label.normal}
                  className={`holom-explorer__hotspot ${active === label.id ? "is-active" : ""}`} type="button" aria-label={t(label.title)} aria-pressed={active === label.id}
                  onClick={(event) => { event.stopPropagation(); select(label); }}><span>{String(index + 1).padStart(2, "0")}</span></button>) : null}
              </model-viewer>
            ) : null}
            {!ready ? <div className="holom-explorer__cover">
              <span className="holom-explorer__cover-number">360°</span>
              <p>{phase === "error" ? text("The 3D view could not load.", "No se pudo cargar la vista 3D.") : text("A new perspective on Holom.", "Una nueva perspectiva de Holom.")}</p>
              <button type="button" className="holom-button" onClick={load} disabled={phase === "loading" || phase === "model"}>
                {phase === "idle" ? text("Explore in 3D", "Explorar en 3D") : phase === "error" ? text("Try again", "Reintentar") : text("Loading aircraft…", "Cargando aeronave…")} <MoveUpRight size={18} />
              </button>
              {phase === "error" ? <small>{text("You can still explore the aircraft details alongside the viewer.", "Puedes seguir consultando los detalles junto al visor.")}</small> : null}
            </div> : null}
            <div className="holom-explorer__views" aria-label={text("Aircraft views", "Vistas de la aeronave")}>
              {[[HOME, text("Perspective", "Perspectiva")], ["180deg 8deg 105%", text("Top", "Superior")], ["90deg 85deg 105%", text("Side", "Lateral")]].map(([value, label]) => <button type="button" key={label} disabled={!ready} onClick={() => changeView(value)}>{label}</button>)}
            </div>
            <div className="holom-explorer__tools">
              <button type="button" disabled={!ready} aria-pressed={rotating} title={text("Automatic rotation", "Rotación automática")} aria-label={text("Automatic rotation", "Rotación automática")} onClick={() => setRotating((value) => !value)}>{rotating ? <Pause /> : <Play />}</button>
              <button type="button" disabled={!ready} aria-pressed={visible} title={text("Show annotations", "Mostrar etiquetas")} aria-label={text("Show annotations", "Mostrar etiquetas")} onClick={() => setVisible((value) => !value)}>{visible ? <Eye /> : <EyeOff />}</button>
              <button type="button" disabled={!ready} title={text("Reset view", "Restablecer vista")} aria-label={text("Reset view", "Restablecer vista")} onClick={() => changeView(HOME)}><RotateCcw /></button>
            </div>
          </div>
          <aside className="holom-explorer__sidebar" aria-label={text("Aircraft details", "Detalles de la aeronave")}>
            <p className="holom-explorer__index-title">{text("EXPLORE THE PLATFORM", "EXPLORA LA PLATAFORMA")} <span>{String(LABELS.length).padStart(2, "0")}</span></p>
            <div className="holom-explorer__index">{LABELS.map((label, index) => <button key={label.id} type="button" className={active === label.id ? "is-active" : ""} aria-pressed={active === label.id} onClick={() => select(label)}><span>{String(index + 1).padStart(2, "0")}</span>{t(label.title)}<MoveUpRight size={16} /></button>)}</div>
            <div className="holom-explorer__detail" aria-live="polite">
              {selected ? <><p className="holom-eyebrow">{text("DESIGNED FOR THE MISSION", "DISEÑADO PARA LA MISIÓN")}</p><h3>{t(selected.title)}</h3><p>{t(selected.description)}</p></> : null}
            </div>
          </aside>
        </div>
        <div className="holom-explorer__footer"><span>{text("Drag to rotate · Scroll or pinch to zoom · Select a point to explore", "Arrastra para girar · Desplaza o pellizca para acercar · Selecciona un punto para explorar")}</span><span>HOLOM / IMAGO AEROSPACE</span></div>
      </div>
    </section>
  );
}
